import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle, Warning, XCircle, Spinner, Rocket, X, EnvelopeSimple, ShieldCheck, ArrowsClockwise } from "@phosphor-icons/react";

const ICONS = {
  pass: { Icon: CheckCircle, color: "text-emerald-600", bg: "bg-emerald-50", ring: "ring-emerald-200", weight: "fill" },
  warn: { Icon: Warning, color: "text-amber-600", bg: "bg-amber-50", ring: "ring-amber-200", weight: "fill" },
  fail: { Icon: XCircle, color: "text-rose-600", bg: "bg-rose-50", ring: "ring-rose-200", weight: "fill" },
};

export function PreflightModal({ campaignId, onClose, onConfirm }) {
  const [data, setData] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [sendingApproval, setSendingApproval] = useState(false);

  const refresh = useCallback(() => api.preflight(campaignId).then(setData), [campaignId]);
  useEffect(() => { refresh(); }, [refresh]);

  const approval = data?.approval || {};
  const approved = !!approval.approved_at && !approval.blocked_reason;

  useEffect(() => {
    if (!data || approved) return;
    const t = setInterval(refresh, 8000);
    return () => clearInterval(t);
  }, [data, approved, refresh]);

  const sendApproval = async () => {
    setSendingApproval(true);
    try {
      const r = await api.sendApproval(campaignId);
      toast.success(`Approval copy emailed to ${r.sent} recipient${r.sent === 1 ? "" : "s"}`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approval send failed");
    } finally {
      setSendingApproval(false);
    }
  };

  const typedOk = confirmText.trim().toUpperCase() === "SEND";
  const canConfirm = data?.can_send && (data.warning_count === 0 || acknowledged) && typedOk;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="preflight-overlay">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg border border-zinc-200 shadow-xl w-full max-w-xl max-h-[90vh] overflow-auto">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-start justify-between gap-3">
          <div>
            <h3 className="font-heading text-base font-medium inline-flex items-center gap-2"><Rocket size={16}/> Pre-send checklist</h3>
            <p className="text-xs text-zinc-500 mt-0.5">A few sanity checks before this campaign goes out.</p>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900" data-testid="preflight-close"><X size={16}/></button>
        </div>

        <div className="p-5">
          {!data ? (
            <div className="flex items-center gap-2 text-sm text-zinc-500 py-6 justify-center">
              <Spinner size={14} className="animate-spin"/> Running checks…
            </div>
          ) : (
            <>
              <ul className="space-y-2" data-testid="preflight-checks">
                {data.checks.map(ch => {
                  const cfg = ICONS[ch.status] || ICONS.pass;
                  return (
                    <li key={ch.id} className={`flex items-start gap-3 p-3 rounded-md ${cfg.bg} ring-1 ${cfg.ring}`} data-testid={`check-${ch.id}`}>
                      <cfg.Icon size={18} weight={cfg.weight} className={`${cfg.color} shrink-0 mt-0.5`}/>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-zinc-900">{ch.label}</div>
                        <div className="text-xs text-zinc-600 mt-0.5">{ch.message}</div>
                      </div>
                    </li>
                  );
                })}
              </ul>

              {/* ===== Approval gate ===== */}
              <div className={`mt-4 rounded-md border p-3 ${approved ? "border-emerald-200 bg-emerald-50/50" : "border-zinc-200 bg-zinc-50"}`} data-testid="approval-section">
                <div className="flex items-center justify-between gap-2">
                  <div className="inline-flex items-center gap-1.5 text-sm font-medium text-zinc-900">
                    <ShieldCheck size={15} weight={approved ? "fill" : "regular"} className={approved ? "text-emerald-600" : "text-zinc-500"}/>
                    Approval gate
                  </div>
                  <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${
                    approved ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : approval.sent_at && !approval.blocked_reason?.includes("edited") ? "border-amber-200 bg-amber-50 text-amber-700"
                    : "border-zinc-200 bg-white text-zinc-600"}`} data-testid="approval-status">
                    {approved ? "Approved" : approval.blocked_reason?.includes("edited") ? "Reset — content changed" : approval.sent_at ? "Awaiting approval" : "Not sent"}
                  </span>
                </div>
                <p className="text-xs text-zinc-600 mt-1.5">
                  {approved
                    ? `Approved by ${approval.approved_by}. You can now send for real.`
                    : `The rendered email goes to ${(approval.recipients || []).join(" and ") || "the approvers"}. Only ${approval.approver_email || "the approver"} can click Approve — the real send stays locked until then.`}
                </p>
                {!approved && (
                  <div className="flex items-center gap-2 mt-2.5">
                    <button
                      data-testid="send-approval-btn"
                      onClick={sendApproval}
                      disabled={sendingApproval}
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-50"
                    >
                      {sendingApproval ? <Spinner size={12} className="animate-spin"/> : <EnvelopeSimple size={12}/>}
                      {approval.sent_at ? "Re-send approval copy" : "Email approval copy"}
                    </button>
                    <button data-testid="approval-refresh" onClick={refresh} className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-zinc-200 hover:bg-white text-zinc-600">
                      <ArrowsClockwise size={12}/> Refresh status
                    </button>
                  </div>
                )}
              </div>

              {data.fail_count > 0 && (
                <div className="mt-4 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-3" data-testid="preflight-failures">
                  Fix the {data.fail_count} failing check{data.fail_count === 1 ? "" : "s"} above before you can send.
                </div>
              )}

              {data.can_send && data.warning_count > 0 && (
                <label className="mt-4 flex items-start gap-2 cursor-pointer p-3 rounded-md border border-amber-200 bg-amber-50/50" data-testid="preflight-ack">
                  <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} className="mt-0.5"/>
                  <span className="text-xs text-amber-900">I understand the {data.warning_count} warning{data.warning_count === 1 ? "" : "s"} above and want to send anyway.</span>
                </label>
              )}

              {data.can_send && (
                <div className="mt-4 p-3 rounded-md border border-zinc-200" data-testid="type-send-section">
                  <div className="text-xs font-medium text-zinc-900 mb-1.5">Final confirmation</div>
                  <input
                    data-testid="type-send-input"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder='Type SEND to unlock'
                    className="h-9 w-full rounded-md border border-zinc-200 px-3 text-sm font-mono tracking-widest uppercase focus:outline-none focus:ring-1 focus:ring-zinc-950"
                  />
                  <p className="text-[11px] text-zinc-500 mt-1">This is a real send to real people — type <span className="font-mono font-semibold">SEND</span> to enable the button.</p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-5 py-4 border-t border-zinc-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-md border border-zinc-200 hover:bg-zinc-50" data-testid="preflight-cancel">Back to edit</button>
          <button
            data-testid="preflight-confirm"
            disabled={!canConfirm}
            onClick={onConfirm}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Rocket size={14}/> Send to {data?.checks?.find(c => c.id === "audience")?.message?.match(/[\d,]+/)?.[0] || "recipients"}
          </button>
        </div>
      </div>
    </div>
  );
}
